import dayjs from "dayjs";

export const CUSTOM_DATE_INTERVAL = "custom" as const;

export type DateIntervalPreset =
  | "today"
  | "this_week"
  | "last_week"
  | "last_7_days"
  | "last_30_days"
  | "this_month"
  | "last_month"
  | "this_year";

export type DateIntervalSelection =
  | DateIntervalPreset
  | typeof CUSTOM_DATE_INTERVAL;

export const DATE_INTERVAL_PRESET_OPTIONS: {
  value: DateIntervalPreset;
  label: string;
}[] = [
  { value: "today", label: "Today" },
  { value: "this_week", label: "This week" },
  { value: "last_week", label: "Last week" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "this_year", label: "This year" },
];

export interface ObservabilityDateRange {
  from: string;
  to: string;
}

const isDateIntervalPreset = (value: string): value is DateIntervalPreset =>
  DATE_INTERVAL_PRESET_OPTIONS.some((option) => option.value === value);

const getThisWeekMonday = (today: dayjs.Dayjs): dayjs.Dayjs => {
  const dayOfWeek = today.day();
  const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  return today.subtract(daysFromMonday, "day");
};

const formatDate = (date: dayjs.Dayjs): string => date.format("YYYY-MM-DD");

export const getDateRangeForInterval = (
  dateInterval: string,
): ObservabilityDateRange => {
  if (!dateInterval || !isDateIntervalPreset(dateInterval)) {
    return { from: "", to: "" };
  }

  const today = dayjs().startOf("day");

  switch (dateInterval) {
    case "today":
      return { from: formatDate(today), to: formatDate(today) };
    case "this_week": {
      const thisWeekMonday = getThisWeekMonday(today);
      return { from: formatDate(thisWeekMonday), to: formatDate(today) };
    }
    case "last_week": {
      const thisWeekMonday = getThisWeekMonday(today);
      const lastWeekMonday = thisWeekMonday.subtract(7, "day");
      const lastWeekSunday = lastWeekMonday.add(6, "day");
      return {
        from: formatDate(lastWeekMonday),
        to: formatDate(lastWeekSunday),
      };
    }
    case "last_7_days":
      return {
        from: formatDate(today.subtract(6, "day")),
        to: formatDate(today),
      };
    case "last_30_days":
      return {
        from: formatDate(today.subtract(29, "day")),
        to: formatDate(today),
      };
    case "this_month":
      return {
        from: formatDate(today.startOf("month")),
        to: formatDate(today),
      };
    case "last_month": {
      const lastMonth = today.subtract(1, "month");
      return {
        from: formatDate(lastMonth.startOf("month")),
        to: formatDate(lastMonth.endOf("month")),
      };
    }
    case "this_year":
      return {
        from: formatDate(today.startOf("year")),
        to: formatDate(today),
      };
  }
};

export const getDateIntervalLabel = (
  dateInterval: string,
  placeholder = "Select date range",
): string => {
  if (dateInterval === CUSTOM_DATE_INTERVAL) {
    return "Custom range";
  }
  const option = DATE_INTERVAL_PRESET_OPTIONS.find(
    (item) => item.value === dateInterval,
  );
  return option?.label ?? placeholder;
};

/** Human-readable range for menus and the closed select trigger. */
export const formatObservabilityDateRangeLabel = (
  from: string,
  to: string,
): string => {
  if (!from || !to) {
    return "";
  }

  const fromDate = dayjs(from, "YYYY-MM-DD", true);
  const toDate = dayjs(to, "YYYY-MM-DD", true);
  if (!fromDate.isValid() || !toDate.isValid()) {
    return "";
  }

  if (from === to) {
    return fromDate.format("MMM D, YYYY");
  }

  if (fromDate.year() === toDate.year()) {
    if (fromDate.month() === toDate.month()) {
      return `${fromDate.format("MMM D")} – ${toDate.format("D, YYYY")}`;
    }
    return `${fromDate.format("MMM D")} – ${toDate.format("MMM D, YYYY")}`;
  }

  return `${fromDate.format("MMM D, YYYY")} – ${toDate.format("MMM D, YYYY")}`;
};

export const getDateIntervalRangeLabel = (
  dateInterval: string,
  customDateFrom = "",
  customDateTo = "",
): string => {
  const range = resolveObservabilityDateRange(
    dateInterval,
    customDateFrom,
    customDateTo,
  );
  return formatObservabilityDateRangeLabel(range.from, range.to);
};

/** Normalize an inclusive custom range so `from` is never after `to`. */
export const normalizeObservabilityDateRange = (
  from: string,
  to: string,
): ObservabilityDateRange => {
  if (!from || !to) {
    return { from, to };
  }
  if (dayjs(from).isAfter(dayjs(to), "day")) {
    return { from: to, to: from };
  }
  return { from, to };
};

export const resolveObservabilityDateRange = (
  dateInterval: string,
  customDateFrom: string,
  customDateTo: string,
): ObservabilityDateRange => {
  if (dateInterval === CUSTOM_DATE_INTERVAL) {
    return normalizeObservabilityDateRange(customDateFrom, customDateTo);
  }
  return getDateRangeForInterval(dateInterval);
};
